
import {Component, Inject, OnInit, ViewEncapsulation, DOCUMENT} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {gaTagMangerProperty} from '@environment';
import {LocalStorageService} from '@portal-commons/providers/local-storage.service';
import {LocalStorageType} from '@portal/models/local-storage-type';
import {CookieService} from 'ngx-cookie-service';
import {NgcCookieConsentService, NgcStatusChangeEvent} from 'ngx-cookieconsent';
import {Subscription} from 'rxjs';

@Component({
    selector: 'tfs-cookie-consent',
    templateUrl: './cookie-consent.component.html',
    styleUrls: ['./cookie-consent.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CookieConsentComponent implements OnInit {
  //keep refs to subscriptions to be able to unsubscribe later
  private popupOpenSubscription: Subscription;
  private popupCloseSubscription: Subscription;
  private initializeSubscription: Subscription;
  private statusChangeSubscription: Subscription;
  private revokeChoiceSubscription: Subscription;
  private noCookieLawSubscription: Subscription;

  constructor(
    private ccService: NgcCookieConsentService,
    private localStorageService: LocalStorageService,
    @Inject(DOCUMENT) document,
    private route: Router,
    private cookieService: CookieService,
  ) {}

  ngOnInit(): void {
    this.statusChangeSubscription = this.ccService?.statusChange$?.subscribe((event: NgcStatusChangeEvent) => {
      if (event.status === 'deny') {
        this.ccService.getConfig().layout = 'custom-layout';
        this.ccService.destroy();
        this.ccService.init(this.ccService.getConfig());
        this.ccService.fadeIn();
        this.setCookieStateFromLocalStorageEntry('analytics');
        this.setCookieStateFromLocalStorageEntry('thirdparty');
        this.addClickEvent('necessary');
        this.addClickEvent('analytics');
        this.addClickEvent('thirdparty');
        document.getElementById('save')?.addEventListener('click', () => {
          this.ccService.close(true);
        });
        document.getElementById('accept_all')?.addEventListener('click', event => {
          (<HTMLInputElement>document.getElementById('analytics')).checked = true;
          (<HTMLInputElement>document.getElementById('thirdparty')).checked = true;
          this.ccService.close(true);
          this.setSelectionCookie();
        });
        document.getElementById('dismiss')?.addEventListener('click', event => {
          (<HTMLInputElement>document.getElementById('analytics')).checked = false;
          (<HTMLInputElement>document.getElementById('thirdparty')).checked = false;
          this.ccService.close(true);
          this.setSelectionCookie();
        });
      }
      if (event.status === 'allow') {
        this.setCookieValue('analytics');
        this.setCookieValue('thirdparty');
        this.setSelectionCookie();
      }
      if (event.status === 'dismiss') {
        this.setCookieValue('analytics', false);
        this.setCookieValue('thirdparty', false);
        this.setSelectionCookie();
      }
    });
    this.route.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (event.url === '/misc/privacypolicy' || event.url === '/misc/imprint') {
          this.ccService.close(true);
        }
      }
    });
  }

  private setSelectionCookie() {
    this.cookieService.set('SHOW_COOKIE_MODAL', 'false', 360, '/', undefined, false, 'Lax');
  }

  private setCookieValue(id: string, state = true) {
    const checkbox = <HTMLInputElement>document.getElementById(id);
    const on = checkbox ? checkbox.checked : state;
    this.localStorageService.setItem(id.toUpperCase(), on.toString(), LocalStorageType.MANDATORY);
    this.toggleTracking(id, on);
  }

  public toggleTracking(tracking: string, on: boolean) {
    if (tracking === 'analytics') {
      if (on) {
        document.cookie = `ga-disable-${gaTagMangerProperty}=true; expires=Thu, 01 Jan 1970 00:00:01 UTC; path=/`;
      } else {
        document.cookie = `ga-disable-${gaTagMangerProperty}=true; expires=Thu, 31 Dec 2099 23:59:59 UTC; path=/`;
      }
      window[`ga-disable-${gaTagMangerProperty}`] = !on;
    }
  }

  private setCookieStateFromLocalStorageEntry(entry) {
    const cookieSettings = this.localStorageService.getItem(entry);
    if (cookieSettings !== null) {
      if (cookieSettings) {
        document.getElementById(entry)?.setAttribute('checked', 'true');
      } else {
        document.getElementById(entry)?.removeAttribute('checked');
      }
    }
  }

  private addClickEvent(entry) {
    document.getElementById('help_' + entry)?.addEventListener('click', event => {
      const expand = <HTMLElement>event.target;
      const helptext = document.getElementById('helptext_' + entry);
      if (helptext?.style?.display) {
        if (helptext.style.display === 'none') {
          helptext.style.display = 'block';
          expand.innerHTML = '&ndash;';
        } else {
          helptext.style.display = 'none';
          expand.innerText = '+';
        }
      }
    });
  }

  ngOnDestroy() {
    // unsubscribe to cookieconsent observables to prevent memory leaks
    this.popupOpenSubscription?.unsubscribe();
    this.popupCloseSubscription?.unsubscribe();
    this.initializeSubscription?.unsubscribe();
    this.statusChangeSubscription?.unsubscribe();
    this.revokeChoiceSubscription?.unsubscribe();
    this.noCookieLawSubscription?.unsubscribe();
  }
}
